import { useEffect, useState } from "react";
import {
  Activity,
  CheckCircle,
  Edit3,
  Map,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useI18n } from "@/hooks/useI18n";
import { useAgencyTrips, useAgencyTripsSummary } from "@/hooks/useAgencyTrips";
import { useAgencyAdvisorMembers } from "@/hooks/useAgencyAdvisorMembers";
import { useTripsListExport } from "@/hooks/useTripsListExport";
import { InfoTooltip } from "@/lib/dashboard/InfoTooltip";
import { TripsFilters } from "@/lib/dashboard/trips/TripsFilters";
import { TripsEmpty, TripsTable } from "@/lib/dashboard/trips/TripsTable";
import { TripDetailSheet } from "@/lib/dashboard/trips/TripDetailSheet";
import { AgencyDashboardPeriod, TripState } from "@/integrations/supabase/rpc-types";
import type { TranslationKey } from "@/types/i18n.types";

type TripsFilter = "all" | TripState;

const PAGE_SIZE = 12;
const SEARCH_DEBOUNCE_MS = 350;

const KPIS: {
  value: TripsFilter;
  labelKey: TranslationKey;
  infoKey: TranslationKey;
  icon: typeof Map;
}[] = [
  { value: "all", labelKey: "dashboard.overview.trips.kpiAll", infoKey: "dashboard.overview.trips.kpiAllInfo", icon: Map },
  { value: "draft", labelKey: "dashboard.overview.trips.kpiDrafts", infoKey: "dashboard.overview.trips.kpiDraftsInfo", icon: Edit3 },
  { value: "in_payment", labelKey: "dashboard.overview.trips.kpiInPayment", infoKey: "dashboard.overview.trips.kpiInPaymentInfo", icon: Activity },
  { value: "completed", labelKey: "dashboard.overview.trips.kpiCompleted", infoKey: "dashboard.overview.trips.kpiCompletedInfo", icon: CheckCircle },
];

interface TripsTabProps {
  /** Selettore della pagina: filtra i viaggi sulla data di creazione. */
  period: AgencyDashboardPeriod;
  /** Admin e agency: gli unici che vedono il lavoro di piu' consulenti. */
  canFilterByAdvisor: boolean;
}

export function TripsTab({ period, canFilterByAdvisor }: TripsTabProps) {
  const { t } = useI18n();
  const [filter, setFilter] = useState<TripsFilter>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [advisorId, setAdvisorId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // "Aggiorna": stessa query, nuova chiave. Lista e contatori ripartono insieme.
  const [refreshKey, setRefreshKey] = useState(0);

  // Un solo debounce per lista e contatori: partono insieme con la stessa ricerca.
  useEffect(() => {
    const timer = setTimeout(
      () => setSearch(searchInput),
      searchInput.trim() ? SEARCH_DEBOUNCE_MS : 0
    );
    return () => clearTimeout(timer);
  }, [searchInput]);

  // La pagina vale solo per la combinazione di filtri in cui e' stata scelta: cambiando un
  // filtro si riparte dalla prima. Legata alla chiave invece che azzerata in un effetto,
  // che farebbe partire prima una chiamata con la pagina vecchia e poi quella giusta.
  const filtersKey = JSON.stringify([period, advisorId, search, filter]);
  const [pageState, setPageState] = useState({ key: filtersKey, page: 1 });
  const page = pageState.key === filtersKey ? pageState.page : 1;
  const setPage = (next: number) => setPageState({ key: filtersKey, page: next });

  const state = filter === "all" ? null : filter;
  const { data, totalCount, loading, refreshing, error, version } = useAgencyTrips(
    {
      period,
      designerId: advisorId,
      search,
      state,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    },
    refreshKey
  );
  const { data: summary, loading: summaryLoading } = useAgencyTripsSummary(
    { period, designerId: advisorId, search },
    refreshKey
  );
  const { data: advisors } = useAgencyAdvisorMembers(period, canFilterByAdvisor);
  const { exporting, exportTrips } = useTripsListExport();

  const hasFilters = filter !== "all" || Boolean(search.trim()) || advisorId !== null;

  const selectedAdvisor = advisors.find((advisor) => advisor.advisor_id === advisorId);
  const selectedAdvisorName = selectedAdvisor
    ? [selectedAdvisor.first_name, selectedAdvisor.last_name].filter(Boolean).join(" ") ||
      selectedAdvisor.email
    : null;

  const selectedRow = data.find((row) => row.itinerary_id === selectedId);

  const handleRefresh = () => setRefreshKey((key) => key + 1);

  if (error) {
    return (
      <Card>
        <CardContent>
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-50 text-red-500">
              <XCircle className="h-6 w-6" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-900">
                {t("dashboard.overview.errorTitle")}
              </p>
              <p className="text-sm text-gray-500">
                {t("dashboard.overview.errorDescription")}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handleRefresh} className="mt-1">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t("dashboard.retry")}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    // Altezza piena: contatori e filtri fissi, la tabella riempie il resto e la
    // paginazione resta ancorata in fondo anche con pochi risultati.
    <div className="flex flex-1 flex-col min-h-0 gap-3">
      <div className="shrink-0 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {KPIS.map(({ value, labelKey, infoKey, icon: Icon }) => {
          const selected = filter === value;
          return (
            <button
              key={value}
              type="button"
              aria-pressed={selected}
              onClick={() => setFilter(value)}
              className={`text-left rounded-lg border px-4 py-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                selected
                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                  : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              <div
                className={`flex items-center gap-1.5 text-xs ${
                  selected ? "text-primary font-medium" : "text-gray-500"
                }`}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{t(labelKey)}</span>
                <InfoTooltip text={t(infoKey)} />
              </div>
              <div className="text-2xl font-semibold mt-1 text-gray-900 tabular-nums">
                {summaryLoading ? (
                  <Skeleton className="h-8 w-12" />
                ) : summary ? (
                  summary[value]
                ) : (
                  "–"
                )}
              </div>
            </button>
          );
        })}
      </div>

      <div className="shrink-0">
        <TripsFilters
          search={searchInput}
          onSearchChange={setSearchInput}
          advisorId={advisorId}
          onAdvisorChange={setAdvisorId}
          advisors={canFilterByAdvisor ? advisors : undefined}
          isRefreshing={loading || refreshing}
          onRefresh={handleRefresh}
          isExporting={exporting}
          filteredCount={totalCount}
          onExportFiltered={() =>
            exportTrips({
              scope: "filtered",
              query: { period, designerId: advisorId, search, state },
              advisorName: selectedAdvisorName,
            })
          }
          onExportAll={() => exportTrips({ scope: "all" })}
        />
      </div>

      <div className="shrink-0 text-sm text-gray-500">
        {loading ? (
          <Skeleton className="h-4 w-24 my-0.5" />
        ) : (
          t("dashboard.overview.trips.count", { count: totalCount })
        )}
      </div>

      <div className="flex-1 min-h-0">
        <TripsTable
          rows={data}
          selectedId={selectedId}
          onSelect={(row) => setSelectedId(row.itinerary_id)}
          showAdvisorColumn={canFilterByAdvisor}
          pageSize={PAGE_SIZE}
          serverPagination={{ page, totalItems: totalCount, onPageChange: setPage, disabled: refreshing }}
          // Skeleton a ogni caricamento (pagina, filtri, aggiorna), non solo al primo.
          isLoading={loading || refreshing}
          animationKey={version}
          emptyState={<TripsEmpty filtered={hasFilters} />}
        />
      </div>

      <TripDetailSheet
        itineraryId={selectedId}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
        fallbackTitle={selectedRow?.title}
      />
    </div>
  );
}

export default TripsTab;
